import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Drawer,
  DrawerProps,
  IconButton,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

/**
 * BasicDrawer - A reusable drawer component with consistent styling
 *
 * @example
 * ```tsx
 * <BasicDrawer
 *   open={isOpen}
 *   onClose={handleClose}
 *   title="Edit Item"
 *   footerActions={[
 *     { title: 'Cancel', variant: 'outlined', onClick: handleClose },
 *     { title: 'Save', variant: 'contained', onClick: handleSave }
 *   ]}
 * >
 *   <YourContent />
 * </BasicDrawer>
 * ```
 */
interface BasicDrawerProps {
  /** Unique identifier for the drawer */
  id?: string
  /** Controls whether the drawer is visible */
  open: boolean
  /** Callback fired when the drawer should close */
  onClose?: () => void
  /** Side from which the drawer will appear */
  anchor?: DrawerProps['anchor']
  /** Width of the drawer (default: 800) */
  width?: number | string
  /** Additional styles for the drawer paper */
  sx?: BoxProps['sx']
  /** Title displayed in the drawer header */
  title?: string
  /** Subtitle displayed below the title */
  subTitle?: string
  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean
  /** Custom props for internal components */
  slotProps?: {
    drawer?: Partial<DrawerProps>
    header?: StackProps
    title?: TypographyProps
    subTitle?: TypographyProps
    content?: BoxProps
  }
  /** Actions to display in the footer */
  footerActions?: Array<{
    title?: ButtonProps['children']
    onClick?: ButtonProps['onClick']
    variant?: ButtonProps['variant']
    color?: ButtonProps['color']
    disabled?: ButtonProps['disabled']
  }>
}

const BasicDrawer: React.FC<React.PropsWithChildren<BasicDrawerProps>> = ({
  id,
  open,
  children,
  onClose,
  anchor = 'right',
  width = 800,
  sx,
  title,
  subTitle,
  showCloseButton = true,
  slotProps,
  footerActions,
}) => {
  return (
    <Drawer
      id={id}
      open={open}
      onClose={onClose}
      anchor={anchor}
      sx={{
        '& .MuiDrawer-paper': {
          width,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      {...slotProps?.drawer}
    >
      {/* Header */}
      {(title || showCloseButton) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            ...slotProps?.header?.sx,
          }}
          {...slotProps?.header}
        >
          <Stack>
            {title && (
              <Typography
                variant="subtitle1"
                component="h3"
                {...slotProps?.title}
                sx={{ fontWeight: 600, ...slotProps?.title?.sx }}
              >
                {title}
              </Typography>
            )}
            {subTitle && (
              <Typography
                variant="body2"
                component="p"
                {...slotProps?.subTitle}
                sx={{ fontWeight: 400, ...slotProps?.subTitle?.sx }}
              >
                {subTitle}
              </Typography>
            )}
          </Stack>

          {showCloseButton && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          scrollbarWidth: 'thin' /* Firefox */,
          scrollbarColor: '#c1c9d2 #f1f3f5' /* thumb track */,
          '&::-webkit-scrollbar': {
            width: 10,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f3f5',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c9d2',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8b2bd',
          },
          ...slotProps?.content?.sx,
        }}
        {...slotProps?.content}
      >
        {children}
      </Box>

      {/* Footer Actions */}
      {footerActions?.length ? (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" justifyContent="flex-end" gap={2}>
            {footerActions.map((action, index) => (
              <Button
                key={index + String(title)}
                variant={action.variant}
                color={action.color}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.title}
              </Button>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Drawer>
  )
}

export default BasicDrawer
